import React, { useEffect, useState } from 'react';
import EmailSearchResults from './EmailSearchResults';
import DesignFileSearchResults from './DesignFileSearchResults';
import DocumentTypeSearchItem from './DocumentTypeSearchItem';
import _ from 'lodash';
import {
  DesignFileSearchResult,
  EmailSearchResult,
  INumberedDocumentViewWithRagFlag,
} from '../../api-client/autogenerated';
import { Collapse, Paper } from '@material-ui/core';
import { Add, Remove } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import EllipsisLoader from '../loader/EllipsisLoader';

export interface IProjectSearchResult {
  projectName: string;
  documents: _.Dictionary<INumberedDocumentViewWithRagFlag[]>;
  emails: EmailSearchResult[];
  designFiles: DesignFileSearchResult[];
}

interface Props {
  results: IProjectSearchResult;
  isLoading: boolean;
  searchQuery: string;
  keywordSearchQueries: string[];
  createdBeforeDate?: string;
  createdAfterDate?: string;
  autoExpand?: boolean;
}

export default function ProjectSearchResults(props: Props) {
  const {
    results,
    isLoading,
    searchQuery,
    keywordSearchQueries,
    createdBeforeDate,
    createdAfterDate,
    autoExpand,
  } = props;

  const [open, setOpen] = useState(!!autoExpand);

  useEffect(() => {
    if (autoExpand) {
      setOpen(true);
    }
  }, [autoExpand]);

  const { projectName, documents, emails, designFiles } = results;

  const allResults = [...Object.values(documents).flat(), ...emails, ...designFiles];

  return (
    <Paper
      style={{
        paddingLeft: 8,
        paddingRight: 8,
        marginBottom: 8,
        boxShadow: '1px 1px 10px 1px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div
        onClick={!isLoading ? () => setOpen((prev) => !prev) : undefined}
        style={{
          display: 'flex',
          position: 'relative',
          alignItems: 'center',
          padding: 16,
          cursor: !isLoading ? 'pointer' : 'default',
          zIndex: 10,
        }}
      >
        <Typography style={{ fontSize: '18px', lineHeight: '24px', fontWeight: 700 }}>
          {projectName} ({allResults.length})
        </Typography>

        <div style={{ display: 'inline-flex', flexGrow: 100 }} />

        {isLoading ? (
          <EllipsisLoader text="" intervalMs={200} fontSize={16} style={{ height: 24 }} />
        ) : (
          <>
            {allResults.length > 0 ? (
              <IconButton style={{ padding: 0 }}>{open ? <Remove /> : <Add />}</IconButton>
            ) : null}
          </>
        )}
      </div>

      <Collapse in={open}>
        {emails.length > 0 ? (
          <EmailSearchResults
            emails={emails}
            searchTerm={searchQuery}
            keywordSearchTerms={keywordSearchQueries}
            filters={{ createdBeforeDate, createdAfterDate }}
          />
        ) : null}

        {designFiles.length > 0 ? (
          <DesignFileSearchResults
            files={designFiles}
            searchTerm={searchQuery}
            keywordSearchTerms={keywordSearchQueries}
          />
        ) : null}

        {Object.entries(documents).map(([documentTemplateId, docs]) => (
          <DocumentTypeSearchItem
            key={documentTemplateId}
            documentTemplateId={documentTemplateId}
            documents={docs}
            searchTerm={searchQuery}
            keywordSearchTerms={keywordSearchQueries}
          />
        ))}
      </Collapse>
    </Paper>
  );
}
